import {buildImageTransformUrl} from '@/ts/utils/pure-functions';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

/** The nine covers the design fans around the circle; the angle between them follows from it. */
const RING_LENGTH = 9;

/** Twice the 142x185 the tile draws a cover at, which is what a hi-DPI screen needs of it. */
const COVER_WIDTH = 284;
const COVER_HEIGHT = 370;

/**
 * The band's pictures, at the size they are drawn: the source assets are up to 1920px wide and ten
 * of them land on this card at once.
 *
 * Distinct, because the same piece can arrive twice in a band assembled from several topics, and
 * two identical covers side by side on the arc read as a rendering fault.
 */
function coverUrls(items: readonly ComponentContent[]): string[] {
  const images = [...new Set(items.map((item) => item.image).filter((id): id is UuidDTO => Boolean(id)))];

  return images.map((id) => buildImageTransformUrl(id, {
    width: COVER_WIDTH,
    height: COVER_HEIGHT,
    format: 'auto',
  }));
}

/**
 * The piece Explore leads with, which is what the card is sending the reader to see.
 *
 * Explore's own rule, from `explore-page.vue`: the one the CMS flags, and the best of the rest if
 * it flags none. Its first choice is a flagged piece inside the reader's partner pack, and that
 * comes from a call this tile does not make — so on a reader who has one, the card and the page
 * can lead with different pieces. Everyone else sees the same one in both places.
 */
function leadCover(items: readonly ComponentContent[]): string {
  const lead = items.find((item) => item.isFeatured) ?? items.find((item) => !item.isFeatured);

  return lead?.image ? coverUrls([lead])[0] ?? '' : '';
}

/**
 * The reader's own recommendation covers for the Explore tile's ring (IPD-3256).
 *
 * As many as the band holds, up to the nine the circle takes. A short band is not thrown away for
 * being short (IPD-3270): the tile fills what is left with its own pictures, and a ring of the
 * reader's content plus a few stock ones is more theirs than nine stock ones. Nothing here knows
 * which is which — the tile pairs them up, because the stock pictures are its own.
 *
 * The cover that drops in is the piece Explore leads with rather than whichever was left over.
 * The card is a door to that page, and one that lands on something a reader then has to go and
 * find — scrolling a rail sideways — has not shown them what it said it would.
 */
export function selectExploreRingCovers(items: readonly ComponentContent[]): ExploreRingCovers {
  const arriving = leadCover(items);
  // It leads, so it is not also on the arc behind itself.
  const covers = coverUrls(items).filter((url) => url !== arriving);

  return {
    ringTiles: covers.slice(0, RING_LENGTH),
    ...(arriving ? {arrivingTile: arriving} : {}),
  };
}

/**
 * The cover for the piece that drops into the ring, read from a band fetched after the reader
 * answered: the one Explore now leads with, or the first of the band that is not already on the
 * arc if the page is leading with something the reader can already see there.
 *
 * Only the arrival is re-read. The nine behind it are the recommendations the reader already had,
 * which is what the card says — their ring, and one piece the platform chose for them since — and
 * re-reading those would mean ten more pictures to download before anything could move.
 *
 * Empty when the ring is the stock one: there is no band behind it for a piece to be new to.
 */
export function selectExploreArrivingCover(
  items: readonly ComponentContent[],
  ringTiles: readonly string[] = [],
): string {
  if (ringTiles.length === 0) {
    return '';
  }
  const onRing = new Set(ringTiles);
  const lead = leadCover(items);
  if (lead && !onRing.has(lead)) {
    return lead;
  }

  return coverUrls(items).find((url) => !onRing.has(url)) ?? '';
}

/** What the tile's ring is drawn with. */
export interface ExploreRingCovers {
  /** In the order they take their places on the circle, however few of them the band holds. */
  ringTiles?: string[];
  /** The cover that drops in, never one the ring already carries. */
  arrivingTile?: string;
}
